import { useAuth } from "@/stores/auth";

export const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

type FetchOpts = {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  token?: string | null;
};

// Dedupes concurrent refresh attempts — several requests can 401 at once
// right after the access token expires; they should all await one refresh
// call, not each fire their own.
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = useAuth.getState().session?.refreshToken;
  if (!refreshToken) return null;
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${BASE}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!res.ok) return null;
        const data = (await res.json()) as TokenResponse;
        useAuth.getState().setSession(data);
        return data.access_token;
      } catch {
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

export async function api<T>(path: string, opts: FetchOpts = {}, _retried = false): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  if (!res.ok) {
    let code = "unknown";
    let message = res.statusText;
    try {
      const data = (await res.json()) as { error?: string; message?: string };
      code = data.error ?? code;
      message = data.message ?? message;
    } catch {
      // non-JSON error body
    }
    // 401 on a request that carried a token means the access token died
    // (expired/revoked) — not a login attempt gone wrong (those calls pass
    // no token). Try a silent refresh-token exchange and retry once before
    // giving up; only clear the session and bounce to login if that fails
    // too, instead of leaving the UI showing a stale/empty page that reads
    // as a data bug.
    if (res.status === 401 && opts.token) {
      if (!_retried) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          return api<T>(path, { ...opts, token: newToken }, true);
        }
      }
      if (!window.location.pathname.startsWith("/auth/")) {
        useAuth.getState().clear();
        window.location.href = "/auth/login";
      }
    }
    throw new ApiError(res.status, code, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// Multipart upload — separate from `api()` since that always JSON-encodes
// the body and sets Content-Type: application/json (which would corrupt a
// multipart boundary). Shares the same 401-refresh-retry behavior.
export async function apiUpload<T>(
  path: string,
  form: FormData,
  token?: string | null,
  _retried = false,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { method: "POST", headers, body: form });

  if (!res.ok) {
    let code = "unknown";
    let message = res.statusText;
    try {
      const data = (await res.json()) as { error?: string; message?: string };
      code = data.error ?? code;
      message = data.message ?? message;
    } catch {
      // non-JSON error body
    }
    if (res.status === 401 && token && !_retried) {
      const newToken = await refreshAccessToken();
      if (newToken) return apiUpload<T>(path, form, newToken, true);
    }
    throw new ApiError(res.status, code, message);
  }
  return (await res.json()) as T;
}

export type TokenResponse = {
  access_token: string;
  refresh_token: string;
  user_id: string;
  kind: "client" | "corporate" | "staff" | "personal";
  roles: string[];
};

export type SupportMessage = {
  id: string;
  full_name: string;
  email: string;
  company: string | null;
  message: string;
  status: "new" | "read" | "resolved" | "escalated";
  created_at: string;
  updated_at: string;
};

export const supportApi = {
  list: (token: string) => api<SupportMessage[]>("/support/messages", { token }),
  updateStatus: (id: string, status: SupportMessage["status"], token: string) =>
    api<SupportMessage>(`/support/messages/${id}`, {
      method: "PATCH",
      body: { status },
      token,
    }),
};

export type VehicleType = { id: string; name: string };

export type Vehicle = {
  id: string;
  type_id: string;
  plate: string;
  make: string | null;
  model: string | null;
  year: number | null;
  status: "active" | "maintenance" | "retired" | "inactive";
  current_driver_id: string | null;
  owner_id: string | null;
  corporate_account_id: string | null;
  capacity_kg: number | null;
  odometer_km: number | null;
  next_service_km: number | null;
  fuel_type: string | null;
  created_at: string;
  updated_at: string;
  device_id: string | null;
  last_lat: number | null;
  last_lng: number | null;
  last_ping_at: string | null;
  /** From GT06 heartbeat (0x13) packets — battery is a 0-6 level, not a %. */
  last_battery_level: number | null;
  last_gsm_signal: number | null;
  last_status_at: string | null;
};


export type Tracker = {
  id: string;
  device_name: string;
  manufacturer: string;
  protocol: string;
  imei: string;
  phone_number: string | null;
  device_password: string | null;
  vehicle_id: string | null;
  vehicle_plate: string | null;
  created_at: string;
  updated_at: string;
  /** Battery (0-6)/signal(0-4)/checkin come from the assigned vehicle's GT06
   * status pings — null when unassigned, there's no data of its own. */
  last_battery_level: number | null;
  last_gsm_signal: number | null;
  last_status_at: string | null;
  last_ping_at: string | null;
};

export const trackersApi = {
  list: (token?: string | null) => api<Tracker[]>("/trackers", { token }),
  get: (id: string, token?: string | null) => api<Tracker>(`/trackers/${id}`, { token }),
  create: (
    body: { device_name: string; manufacturer: string; protocol: string; imei: string; phone_number?: string; device_password?: string },
    token?: string | null,
  ) => api<Tracker>("/trackers", { method: "POST", body, token }),
  update: (id: string, body: Partial<Tracker>, token?: string | null) =>
    api<Tracker>(`/trackers/${id}`, { method: "PATCH", body, token }),
  remove: (id: string, token?: string | null) => api<void>(`/trackers/${id}`, { method: "DELETE", token }),
  assign: (id: string, vehicleId: string, token?: string | null) =>
    api<Tracker>(`/trackers/${id}/assign`, { method: "POST", body: { vehicle_id: vehicleId }, token }),
  unassign: (id: string, token?: string | null) =>
    api<Tracker>(`/trackers/${id}/unassign`, { method: "POST", token }),
};

export type MaintenanceRecord = {
  id: string;
  vehicle_id: string;
  scheduled_for: string;
  kind: string;
  notes: string | null;
  completed_at: string | null;
  created_at: string;
};

export const vehiclesApi = {
  listTypes: () => api<VehicleType[]>("/vehicle-types"),
  list: (token?: string | null) => api<Vehicle[]>("/vehicles", { token }),
  get: (id: string, token?: string | null) => api<Vehicle>(`/vehicles/${id}`, { token }),
  create: (body: Partial<Vehicle> & { type_id: string; plate: string }, token?: string | null) =>
    api<Vehicle>("/vehicles", { method: "POST", body, token }),
  update: (id: string, body: Partial<Vehicle>, token?: string | null) =>
    api<Vehicle>(`/vehicles/${id}`, { method: "PATCH", body, token }),
  listMaintenance: (vehicleId: string, token?: string | null) =>
    api<MaintenanceRecord[]>(`/vehicles/${vehicleId}/maintenance`, { token }),
  createMaintenance: (
    vehicleId: string,
    body: { scheduled_for: string; kind: string; notes?: string },
    token?: string | null,
  ) => api<MaintenanceRecord>(`/vehicles/${vehicleId}/maintenance`, { method: "POST", body, token }),
  /** Links a GPS tracker to this vehicle by its hardware IMEI. */
  setDevice: (vehicleId: string, deviceId: string, token?: string | null) =>
    api<{ device_id: string }>(`/vehicles/${vehicleId}/device`, { method: "POST", body: { device_id: deviceId }, token }),
  /** GPS pings, oldest first — rolling last 24h, or a specific UTC day if `date` given. */
  gpsHistory: (vehicleId: string, date?: string, token?: string | null) =>
    api<GpsPing[]>(`/vehicles/${vehicleId}/gps-history${date ? `?date=${date}` : ""}`, { token }),
  /** GPS pings, oldest first, over an arbitrary range (max 7 days) — used by replay. `to` is exclusive. */
  gpsHistoryRange: (vehicleId: string, from: string, to: string, token?: string | null) =>
    api<GpsPing[]>(
      `/vehicles/${vehicleId}/gps-history?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      { token },
    ),
};

export type GpsPing = { lat: number; lng: number; speed_kmh: number | null; recorded_at: string };

export type Parcel = {
  id: string;
  tracking_id: string;
  status: "intake" | "assigned" | "picked" | "in_transit" | "delivered" | "failed" | "cancelled";
  amount_kobo: number;
  receiver_name: string | null;
  pickup_addr: string | null;
  dropoff_addr: string | null;
  driver_id: string | null;
  client_id: string | null;
  corporate_account_id: string | null;
  item_description: string | null;
  pickup_lat: number | null;
  pickup_lng: number | null;
  dropoff_lat: number | null;
  dropoff_lng: number | null;
  /** NIPOST-verified postcode — only set when an admin has postcode
   * enrichment switched on (Settings > App settings). */
  pickup_postcode: string | null;
  dropoff_postcode: string | null;
  /** Goods-in-transit insurance (MyCover.ai On Demand GIT). Auto-quoted on
   * create; "active" once purchased via parcelsApi.purchaseInsurance. */
  insurance_status: "unavailable" | "quoted" | "active" | "failed";
  insurance_premium_kobo: number | null;
  insurance_policy_id: string | null;
  insurance_policy_number: string | null;
  insurance_certificate_url: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type ParcelEvent = { kind: string; occurred_at: string; payload: unknown };
export type ParcelLocation = { lat: number; lng: number; recorded_at: string } | null;

export type CreateParcelReq = {
  sender_name: string;
  sender_email?: string;
  sender_phone?: string;
  receiver_name: string;
  receiver_email?: string;
  receiver_phone?: string;
  pickup_addr: string;
  dropoff_addr: string;
  amount_kobo: number;
  driver_id?: string;
  client_id?: string;
  corporate_account_id?: string;
  item_description?: string;
};

export const parcelsApi = {
  list: (filters?: { clientId?: string; corporateAccountId?: string }, token?: string | null) => {
    const params = new URLSearchParams();
    if (filters?.clientId) params.set("client_id", filters.clientId);
    if (filters?.corporateAccountId) params.set("corporate_account_id", filters.corporateAccountId);
    const qs = params.toString();
    return api<Parcel[]>(`/parcels${qs ? `?${qs}` : ""}`, { token });
  },
  get: (id: string, token?: string | null) => api<Parcel>(`/parcels/${id}`, { token }),
  create: (body: CreateParcelReq, token?: string | null) => api<Parcel>("/parcels", { method: "POST", body, token }),
  update: (id: string, body: Partial<CreateParcelReq>, token?: string | null) =>
    api<Parcel>(`/parcels/${id}`, { method: "PATCH", body, token }),
  events: (id: string, token?: string | null) => api<ParcelEvent[]>(`/parcels/${id}/events`, { token }),
  location: (id: string, token?: string | null) => api<ParcelLocation>(`/parcels/${id}/location`, { token }),
  /** GPS history of the vehicle carrying this parcel (via its assigned driver) — rolling last 24h, or a specific UTC day if `date` given. */
  gpsHistory: (id: string, date?: string, token?: string | null) =>
    api<GpsPing[]>(`/parcels/${id}/gps-history${date ? `?date=${date}` : ""}`, { token }),
  purchaseInsurance: (
    id: string,
    body: { gender: string; date_of_birth: string; address: string; vehicle_plate_number: string },
    token?: string | null,
  ) => api<Parcel>(`/parcels/${id}/insurance/purchase`, { method: "POST", body, token }),
  images: (id: string, token?: string | null) => api<ParcelImage[]>(`/parcels/${id}/images`, { token }),
  uploadImage: (id: string, file: File, kind?: string, token?: string | null) => {
    const form = new FormData();
    form.append("file", file);
    if (kind) form.append("kind", kind);
    return apiUpload<ParcelImage>(`/parcels/${id}/images`, form, token);
  },
  scan: (id: string, event: string, note?: string, token?: string | null) =>
    api<{ id: string; status: string; event: string }>(`/parcels/${id}/scan`, { method: "POST", body: { event, note }, token }),
  requestDeliveryOtp: (id: string, token?: string | null) =>
    api<{ status: string; channel: "sms" | "email" }>(`/parcels/${id}/delivery-otp/request`, { method: "POST", token }),
  verifyDeliveryOtp: (id: string, code: string, token?: string | null) =>
    api<{ verified: boolean }>(`/parcels/${id}/delivery-otp/verify`, { method: "POST", body: { code }, token }),
  qrUrl: (trackingId: string) => `${BASE}/parcels/track/${trackingId}/qr`,
  unassignedByZone: (token?: string | null) =>
    api<{
      zones: Array<{
        zone: string;
        parcels: Array<{ id: string; tracking_id: string; pickup_addr: string | null; dropoff_addr: string | null }>;
      }>;
    }>("/parcels/unassigned-by-zone", { token }),
  bulkAssign: (parcelIds: string[], driverId: string, token?: string | null) =>
    api<{ updated: number }>("/parcels/bulk-assign", {
      method: "POST",
      body: { parcel_ids: parcelIds, driver_id: driverId },
      token,
    }),
};

export type ParcelImage = { id: string; kind: string; url: string | null; created_at: string };

export type Wallet = { id: string; available_kobo: number; pending_kobo: number; currency: string };
export type WalletTransaction = {
  id: string;
  reference: string;
  type: "topup" | "payout" | "delivery_fee" | "refund";
  provider: string;
  amount_kobo: number;
  status: "success" | "pending" | "failed";
  method: string | null;
  parcel_id: string | null;
  occurred_at: string;
};

export const walletApi = {
  get: (userId?: string, token?: string | null) => api<Wallet>(`/wallet${userId ? `?user_id=${userId}` : ""}`, { token }),
  transactions: (userId?: string, token?: string | null) =>
    api<WalletTransaction[]>(`/wallet/transactions${userId ? `?user_id=${userId}` : ""}`, { token }),
};

export const paymentsApi = {
  initPaystack: (amountKobo: number, token?: string | null) =>
    api<{ email: string; amount_kobo: number; reference: string }>("/payments/initialize/paystack", {
      method: "POST",
      body: { amount_kobo: amountKobo },
      token,
    }),
  verifyPaystack: (reference: string, token?: string | null) =>
    api<Wallet>("/payments/verify/paystack", { method: "POST", body: { reference }, token }),
  initStripe: (amountKobo: number, token?: string | null) =>
    api<{ client_secret: string }>("/payments/initialize/stripe", { method: "POST", body: { amount_kobo: amountKobo }, token }),
  verifyStripe: (paymentIntentId: string, token?: string | null) =>
    api<Wallet>("/payments/verify/stripe", { method: "POST", body: { payment_intent_id: paymentIntentId }, token }),
};

export type PayoutAccount = { id: string; bank_name: string; account_number: string; account_name: string };

export const payoutAccountsApi = {
  list: (token?: string | null) => api<PayoutAccount[]>("/wallet/payout-accounts", { token }),
  create: (data: Omit<PayoutAccount, "id">, token?: string | null) =>
    api<PayoutAccount>("/wallet/payout-accounts", { method: "POST", body: data, token }),
  remove: (id: string, token?: string | null) => api<void>(`/wallet/payout-accounts/${id}`, { method: "DELETE", token }),
};

export type PayoutRequest = {
  id: string;
  user_id: string;
  requester_name: string | null;
  amount_kobo: number;
  status: "pending" | "approved" | "rejected" | "paid";
  bank_name: string;
  account_number: string;
  account_name: string;
  notes: string | null;
  created_at: string;
};

export const payoutRequestsApi = {
  create: (payoutAccountId: string, amountKobo: number, token?: string | null) =>
    api<PayoutRequest>("/wallet/payout-requests", { method: "POST", body: { payout_account_id: payoutAccountId, amount_kobo: amountKobo }, token }),
  list: (token?: string | null) => api<PayoutRequest[]>("/payout-requests", { token }),
  review: (id: string, status: "approved" | "rejected" | "paid", notes: string | undefined, token?: string | null) =>
    api<void>(`/payout-requests/${id}`, { method: "PATCH", body: { status, notes }, token }),
};

export type Notification = {
  id: string;
  severity: "critical" | "warning" | "info";
  title: string;
  detail: string | null;
  read_at: string | null;
  created_at: string;
};

export const notificationsApi = {
  list: (userId?: string, token?: string | null) =>
    api<Notification[]>(`/notifications${userId ? `?user_id=${userId}` : ""}`, { token }),
  markRead: (id: string, token?: string | null) => api<void>(`/notifications/${id}/read`, { method: "PATCH", token }),
};

export type Conversation = {
  id: string;
  parcel_id: string | null;
  counterpart_name: string;
  counterpart_role: "driver" | "customer" | "support";
  counterpart_initials: string | null;
  last_message: string | null;
  last_at: string | null;
  unread: number;
};

export type ConversationMessage = { id: string; sender: "me" | "them" | string; body: string; created_at: string };

export const conversationsApi = {
  list: (token?: string | null) => api<Conversation[]>("/conversations", { token }),
  messages: (id: string, token?: string | null) => api<ConversationMessage[]>(`/conversations/${id}/messages`, { token }),
  postMessage: (id: string, body: string, token?: string | null) =>
    api<ConversationMessage>(`/conversations/${id}/messages`, { method: "POST", body: { body }, token }),
  forParcel: (parcelId: string, counterpart: "client" | "driver", token?: string | null) =>
    api<Conversation>("/conversations/for-parcel", { method: "POST", body: { parcel_id: parcelId, counterpart }, token }),
};

export type DispatchQueueItem = {
  id: string;
  tracking_id: string;
  pickup_addr: string;
  dropoff_addr: string;
  pickup_lat: number | null;
  pickup_lng: number | null;
  amount_kobo: number;
  created_at: string;
  waiting_minutes: number;
};

export type DispatchAssignment = {
  id: string;
  parcel_id: string;
  driver_id: string;
  vehicle_id: string | null;
  status: "pending" | "accepted" | "declined" | "cancelled";
  assigned_at?: string;
  responded_at: string | null;
  assigned_by?: string;
  note?: string | null;
};

export type MyQueueItem = {
  assignment_id: string;
  parcel_id: string;
  tracking_id: string;
  pickup_addr: string;
  dropoff_addr: string;
  amount_kobo: number;
  assigned_at: string;
};

export const dispatchApi = {
  myQueue: (token?: string | null) => api<MyQueueItem[]>("/dispatch/my-queue", { token }),
  queue: (filters?: { corporateAccountId?: string; limit?: number; offset?: number }, token?: string | null) => {
    const params = new URLSearchParams();
    if (filters?.corporateAccountId) params.set("corporate_account_id", filters.corporateAccountId);
    if (filters?.limit) params.set("limit", String(filters.limit));
    if (filters?.offset) params.set("offset", String(filters.offset));
    const qs = params.toString();
    return api<{ items: DispatchQueueItem[]; total: number }>(`/dispatch/queue${qs ? `?${qs}` : ""}`, { token });
  },
  assign: (parcelId: string, body: { driver_id: string; vehicle_id?: string | null; note?: string }, token?: string | null) =>
    api<DispatchAssignment>(`/dispatch/parcels/${parcelId}/assign`, { method: "POST", body, token }),
  unassign: (parcelId: string, reason?: string, token?: string | null) =>
    api<void>(`/dispatch/parcels/${parcelId}/unassign`, { method: "POST", body: { reason }, token }),
  bulkAssign: (
    assignments: { parcel_id: string; driver_id: string; vehicle_id?: string | null }[],
    token?: string | null,
  ) => api<{ succeeded: string[]; failed: { parcel_id: string; error: string }[] }>("/dispatch/parcels/bulk-assign", { method: "POST", body: { assignments }, token }),
  accept: (parcelId: string, token?: string | null) =>
    api<DispatchAssignment>(`/dispatch/parcels/${parcelId}/accept`, { method: "POST", token }),
  decline: (parcelId: string, reason?: string, token?: string | null) =>
    api<DispatchAssignment>(`/dispatch/parcels/${parcelId}/decline`, { method: "POST", body: { reason }, token }),
  history: (parcelId: string, token?: string | null) =>
    api<DispatchAssignment[]>(`/dispatch/parcels/${parcelId}/history`, { token }),
};

export type DriverDirectoryEntry = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  vehicle_plate: string | null;
  status: string;
  rating: string;
  active_parcels: number;
  completed_today: number;
  last_location: string | null;
  corporate_account_id: string | null;
  activated: boolean;
};

/** `staff_role` values (see backend/migrations/0001_initial.sql) — the real
 * RBAC roles, distinct from the level org-chart field below.
 * "driver" stays a legal value here for displaying existing rows, but is
 * excluded from STAFF_ROLES (the Add Staff dropdown) — drivers are created
 * via POST /drivers, not the staff form. "support" can be picked — assist
 * only, blocked server-side from creating clients/corporates/drivers/
 * vehicles (see backend deny_support()). */
export type StaffRole = "superadmin" | "admin" | "dispatch_officer" | "driver" | "support";
export const STAFF_ROLES: StaffRole[] = ["superadmin", "admin", "dispatch_officer", "support"];

export type StaffDirectoryEntry = {
  id: string;
  name: string;
  email: string | null;
  level: number;
  job_title: string | null;
  joined_at: string | null;
  status: string;
  roles: StaffRole[];
};

export type ClientDirectoryEntry = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: string;
  created_at: string;
  total_shipments: number;
  total_spent_kobo: number;
  activated: boolean;
};

export type PersonalDirectoryEntry = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: string;
  created_at: string;
  vehicle_count: number;
};

export type CorporateMember = {
  id: string;
  name: string;
  email: string;
  role: string;
  user_id: string | null;
  user_status: string | null;
  email_verified: boolean;
  activated: boolean;
};
export type CorporateAccount = {
  id: string;
  /** Login-capable users.id for this corporate, if onboarded with a contact email. */
  user_id: string | null;
  company: string;
  industry: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  monthly_volume: number;
  contract_value_kobo: number;
  status: string;
  since: string | null;
  members: CorporateMember[];
};

export const directoryApi = {
  drivers: (token?: string | null) => api<DriverDirectoryEntry[]>("/drivers", { token }),
  createDriver: (
    body: { full_name: string; email?: string; phone?: string; vehicle_id?: string; corporate_account_id?: string; password: string },
    token?: string | null,
  ) => api<DriverDirectoryEntry>("/drivers", { method: "POST", body, token }),
  updateDriver: (
    id: string,
    body: Partial<{ full_name: string; email: string; phone: string; status: string; vehicle_id: string; corporate_account_id: string; password: string }>,
    token?: string | null,
  ) => api<DriverDirectoryEntry>(`/drivers/${id}`, { method: "PATCH", body, token }),
  deleteDriver: (id: string, token?: string | null) => api<void>(`/drivers/${id}`, { method: "DELETE", token }),

  staff: (token?: string | null) => api<StaffDirectoryEntry[]>("/staff", { token }),
  createStaff: (body: { full_name: string; email: string; role: StaffRole; password: string }, token?: string | null) =>
    api<StaffDirectoryEntry>("/staff", { method: "POST", body, token }),
  updateStaff: (
    id: string,
    body: Partial<{ full_name: string; email: string; status: string; role: StaffRole; password: string }>,
    token?: string | null,
  ) => api<StaffDirectoryEntry>(`/staff/${id}`, { method: "PATCH", body, token }),
  deleteStaff: (id: string, token?: string | null) => api<void>(`/staff/${id}`, { method: "DELETE", token }),

  clients: (token?: string | null) => api<ClientDirectoryEntry[]>("/clients", { token }),
  client: (id: string, token?: string | null) => api<ClientDirectoryEntry>(`/clients/${id}`, { token }),
  createClient: (body: { full_name: string; email?: string; phone?: string }, token?: string | null) =>
    api<ClientDirectoryEntry>("/clients", { method: "POST", body, token }),
  updateClient: (
    id: string,
    body: Partial<{ full_name: string; email: string; phone: string; status: string }>,
    token?: string | null,
  ) => api<ClientDirectoryEntry>(`/clients/${id}`, { method: "PATCH", body, token }),
  deleteClient: (id: string, token?: string | null) => api<void>(`/clients/${id}`, { method: "DELETE", token }),

  personal: (token?: string | null) => api<PersonalDirectoryEntry[]>("/personal", { token }),
  personalOne: (id: string, token?: string | null) => api<PersonalDirectoryEntry>(`/personal/${id}`, { token }),
  createPersonal: (body: { full_name: string; email?: string; phone?: string }, token?: string | null) =>
    api<PersonalDirectoryEntry>("/personal", { method: "POST", body, token }),
  updatePersonal: (
    id: string,
    body: Partial<{ full_name: string; email: string; phone: string; status: string }>,
    token?: string | null,
  ) => api<PersonalDirectoryEntry>(`/personal/${id}`, { method: "PATCH", body, token }),

  corporates: (token?: string | null) => api<CorporateAccount[]>("/corporates", { token }),
  myCorporate: (token?: string | null) => api<CorporateAccount>("/corporates/me", { token }),
  corporate: (id: string, token?: string | null) => api<CorporateAccount>(`/corporates/${id}`, { token }),
  createCorporate: (
    body: {
      company: string;
      industry?: string;
      contact_name?: string;
      contact_email?: string;
      contact_phone?: string;
      monthly_volume?: number;
      contract_value_kobo?: number;
      status?: string;
      owner: { name: string; email: string; password: string };
    },
    token?: string | null,
  ) => api<CorporateAccount>("/corporates", { method: "POST", body, token }),
  updateCorporate: (
    id: string,
    body: Partial<{
      company: string;
      industry: string;
      contact_name: string;
      contact_email: string;
      contact_phone: string;
      monthly_volume: number;
      contract_value_kobo: number;
      status: string;
    }>,
    token?: string | null,
  ) => api<CorporateAccount>(`/corporates/${id}`, { method: "PATCH", body, token }),
  addCorporateMember: (
    id: string,
    body: { name: string; email: string; role?: string; password?: string },
    token?: string | null,
  ) => api<CorporateAccount>(`/corporates/${id}/members`, { method: "POST", body, token }),
  updateCorporateMember: (
    id: string,
    memberId: string,
    body: Partial<{ name: string; email: string; role: string; password: string }>,
    token?: string | null,
  ) => api<CorporateAccount>(`/corporates/${id}/members/${memberId}`, { method: "PATCH", body, token }),
  removeCorporateMember: (id: string, memberId: string, token?: string | null) =>
    api<CorporateAccount>(`/corporates/${id}/members/${memberId}`, { method: "DELETE", token }),
  setCorporateMemberStatus: (id: string, memberId: string, blocked: boolean, token?: string | null) =>
    api<CorporateAccount>(`/corporates/${id}/members/${memberId}/status`, { method: "PATCH", body: { blocked }, token }),
  resendCorporateMemberActivation: (id: string, memberId: string, token?: string | null) =>
    api<{ status: string }>(`/corporates/${id}/members/${memberId}/resend-activation`, { method: "POST", token }),
};

export type AnalyticsSummary = {
  active_deliveries: number;
  completed_today: number;
  on_time_rate: number;
  active_drivers: number;
  revenue_today_kobo: number;
  exceptions: number;
};

export type AnalyticsDaily = {
  deliveries: { date: string; count: number }[];
  revenue_kobo: { date: string; amount_kobo: number }[];
};

export type AnalyticsPostcodeZones = { zones: { zone: string; count: number }[] };

export const analyticsApi = {
  summary: (token?: string | null) => api<AnalyticsSummary>("/analytics/summary", { token }),
  daily: (token?: string | null) => api<AnalyticsDaily>("/analytics/daily", { token }),
  postcodeZones: (token?: string | null) => api<AnalyticsPostcodeZones>("/analytics/postcode-zones", { token }),
};

export type UserProfile = {
  id: string;
  user_type: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  status: string;
  photo_url?: string | null;
};

export const usersApi = {
  get: (id: string, token?: string | null) => api<UserProfile>(`/users/${id}`, { token }),
  update: (
    id: string,
    body: Partial<{ full_name: string; phone: string; email: string; status: string }>,
    token?: string | null,
  ) => api<UserProfile>(`/users/${id}`, { method: "PATCH", body, token }),
  uploadPhoto: (id: string, file: File, token?: string | null) => {
    const form = new FormData();
    form.append("file", file);
    return apiUpload<UserProfile>(`/users/${id}/photo`, form, token);
  },
  resendActivation: (id: string, token?: string | null) =>
    api<{ status: string }>(`/users/${id}/resend-activation`, { method: "POST", token }),
  confirmEmailChange: (token: string) =>
    api<{ status: string; email: string }>("/users/email-change/confirm", {
      method: "POST",
      body: { token },
    }),
};

export type GeocodeSearchResult = { label: string; lat: number; lng: number };

export const settingsApi = {
  get: (token?: string | null) => api<Record<string, unknown>>("/settings", { token }),
  setLocationSearch: (enabled: boolean, token?: string | null) =>
    api<{ enabled: boolean }>("/settings/location-search", { method: "PATCH", body: { enabled }, token }),
  setPostcodeEnrichment: (enabled: boolean, token?: string | null) =>
    api<{ enabled: boolean }>("/settings/postcode-enrichment", { method: "PATCH", body: { enabled }, token }),
  postcodeStatus: (token?: string | null) =>
    api<{
      key_configured: boolean;
      key_valid: boolean | null;
      error: string | null;
      toggle_enabled: boolean;
      active: boolean;
      base_url: string;
    }>("/postcode/status", { token }),
  searchLocation: (q: string, token?: string | null) =>
    api<GeocodeSearchResult[]>(`/geocode/search?q=${encodeURIComponent(q)}`, { token }),
};

export type PostcodeSuggestion = { code: string; label: string };

export const postcodeApi = {
  autocomplete: (q: string, token?: string | null) =>
    api<PostcodeSuggestion[]>(`/postcode/autocomplete?q=${encodeURIComponent(q)}`, { token }),
  lookup: (code: string, token?: string | null) =>
    api<{
      postcode: string;
      valid: boolean;
      administrative_address: unknown;
      recent_house_address: unknown;
      resolved_address: string | null;
    }>(`/postcode/lookup?code=${encodeURIComponent(code)}`, { token }),
  bulkValidate: (addresses: string[], token?: string | null) =>
    api<{
      results: Array<{
        address: string;
        status: "ok" | "no_postcode" | "error" | "skipped";
        lat?: number;
        lng?: number;
        postcode?: string;
        error?: string;
      }>;
    }>("/postcode/bulk-validate", { method: "POST", body: { addresses }, token }),
  zoneRates: (token?: string | null) =>
    api<Array<{ zone: string; fee_multiplier: number; label: string | null }>>("/postcode/zone-rates", { token }),
  setZoneRate: (zone: string, feeMultiplier: number, label: string | null, token?: string | null) =>
    api<{ zone: string; fee_multiplier: number; label: string | null }>("/postcode/zone-rates", {
      method: "PUT",
      body: { zone, fee_multiplier: feeMultiplier, label },
      token,
    }),
  zoneSuggestion: (zone: string, token?: string | null) =>
    api<{
      zone: string;
      fee_multiplier: number;
      label: string | null;
      suggested_driver: { id: string; name: string } | null;
    }>(`/postcode/zone-suggestion?zone=${encodeURIComponent(zone)}`, { token }),
};

export const authApi = {
  requestOtp: (identifier: string) =>
    api<{ status: string; channel: "sms" | "email"; ttl_minutes: number }>(
      "/auth/request-otp",
      { method: "POST", body: { identifier } },
    ),
  verify: (identifier: string, code: string) =>
    api<TokenResponse>("/auth/verify", {
      method: "POST",
      body: { identifier, code },
    }),
  refresh: (refresh_token: string) =>
    api<TokenResponse>("/auth/refresh", {
      method: "POST",
      body: { refresh_token },
    }),
  logout: (refresh_token: string) =>
    api<{ status: string }>("/auth/logout", {
      method: "POST",
      body: { refresh_token },
    }),
  linkedinRedirect: () => api<{ url: string }>("/auth/linkedin/redirect"),
  linkedinCallback: (code: string) =>
    api<TokenResponse>("/auth/linkedin/callback", { method: "POST", body: { code } }),
  appleRedirect: () => api<{ url: string }>("/auth/apple/redirect"),
  googleRedirect: () => api<{ url: string }>("/auth/google/redirect"),
  googleCallback: (code: string) =>
    api<TokenResponse>("/auth/google/callback", { method: "POST", body: { code } }),
  microsoftRedirect: () => api<{ url: string }>("/auth/microsoft/redirect"),
  microsoftCallback: (code: string) =>
    api<TokenResponse>("/auth/microsoft/callback", { method: "POST", body: { code } }),
  handoff: (token: string) => api<TokenResponse>(`/auth/handoff/${token}`),
  register: (body: {
    full_name: string;
    email: string;
    password: string;
    account_type: "individual" | "company";
    office_address?: string;
    registration_no?: string;
    company_type?: string;
    tin?: string;
    company_phone?: string;
  }) =>
    api<{ status: string; email: string }>("/auth/register", {
      method: "POST",
      body,
    }),
  login: (email: string, password: string) =>
    api<TokenResponse>("/auth/login", { method: "POST", body: { email, password } }),
  confirmEmail: (token: string) =>
    api<TokenResponse>("/auth/confirm-email", { method: "POST", body: { token } }),
  activate: (token: string, password: string) =>
    api<TokenResponse>("/auth/activate", { method: "POST", body: { token, password } }),
  resendConfirmation: (email: string) =>
    api<{ status: string }>("/auth/resend-confirmation", {
      method: "POST",
      body: { email },
    }),
};
